/**
 * ``useObjectTrackDetail`` — TanStack Query hook around
 * ``GET /api/query/object-track/{track_id}`` (ADR-041; REQ-QUERY-055): the
 * track summary plus the related tracks (other sensors reporting the same
 * ``object_id`` — a relation, never fusion; REQ-UI-177).
 *
 * Mirrors :func:`entity-details/use-detection-detail`: a 404 resolves to
 * ``null`` (an aged-out track is an empty state, not an error), 401/403/404
 * never retry, the session must hold a read role.
 *
 * Read by the track Entity Details sections (REQ-UI-174) and by the
 * detection-side membership line (REQ-UI-175), which passes the detection's
 * ``track_id``.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type ObjectTrackDetail = components["schemas"]["ObjectTrackDetailResponse"];

export const OBJECT_TRACK_DETAIL_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** A live track's summary moves every frame; keep the detail fresh-ish. */
export const OBJECT_TRACK_DETAIL_STALE_TIME_MS = 10_000;

export function objectTrackDetailQueryKey(trackId: string): readonly unknown[] {
  return ["object-track-detail", trackId] as const;
}

export interface UseObjectTrackDetailOptions {
  /** ``null`` / empty disables the query. */
  trackId: string | null;
}

export function useObjectTrackDetail(
  options: UseObjectTrackDetailOptions,
): UseQueryResult<ObjectTrackDetail | null, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(OBJECT_TRACK_DETAIL_READER_ROLES);
  const trackId = options.trackId !== null && options.trackId.length > 0 ? options.trackId : null;
  const enabled = trackId !== null && hasRole;

  return useQuery<ObjectTrackDetail | null, ApiError>({
    queryKey:
      trackId !== null ? objectTrackDetailQueryKey(trackId) : ["object-track-detail", "disabled"],
    queryFn: async ({ signal }) => {
      if (trackId === null) {
        throw new ApiError(0, null, "useObjectTrackDetail: query ran without trackId");
      }
      const url = `/api/query/object-track/${encodeURIComponent(trackId)}`;
      try {
        const raw = await apiFetch<unknown>(url, { signal });
        if (!isObjectTrackDetail(raw)) {
          console.error(
            "[meridian/entity-details/use-object-track-detail] response failed shape validation",
            raw,
          );
          throw new ApiError(500, raw, "object-track response did not match the expected shape");
        }
        return raw;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled,
    staleTime: OBJECT_TRACK_DETAIL_STALE_TIME_MS,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

function isObjectTrackDetail(value: unknown): value is ObjectTrackDetail {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.related) || typeof obj.duration_ms !== "number") return false;
  const summary = obj.summary;
  if (typeof summary !== "object" || summary === null) return false;
  const s = summary as Record<string, unknown>;
  return (
    typeof s.track_id === "string" &&
    typeof s.producer_id === "string" &&
    typeof s.object_id === "string" &&
    typeof s.first_time === "string" &&
    typeof s.last_time === "string" &&
    typeof s.detection_count === "number"
  );
}
